import React from "react";
import { Box, Stack, Typography } from "@mui/material";

const ChatSkeleton = () => {
  const animations = `
    @keyframes morph {
      0%, 100% { border-radius: 42% 58% 70% 30% / 45% 45% 55% 55%; transform: scale(1); }
      25% { border-radius: 70% 30% 46% 54% / 30% 29% 71% 70%; transform: scale(1.1); }
      50% { border-radius: 50% 50% 34% 66% / 56% 68% 32% 44%; transform: scale(1.2); }
      75% { border-radius: 46% 54% 50% 50% / 35% 61% 39% 65%; transform: scale(1.1); }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  const ShimmerBox = ({ width, height, borderRadius = "8px", mb = 0 }) => (
    <Box
      sx={{
        width,
        height,
        mb,
        borderRadius,
        position: "relative",
        overflow: "hidden",
        bgcolor: "#f0f2f5",
        "&::after": {
          content: "\"\"",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
          animation: "shimmer 2s infinite",
        },
      }}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        width: "100%",
        bgcolor: "#f8f9fa",
        overflow: "hidden",
      }}
    >
      <style>{animations}</style>

      {/* Premium Sidebar */}
      <Box
        sx={{
          width: 380,
          p: 3,
          bgcolor: "white",
          borderRight: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Stack spacing={4}>
          <ShimmerBox width="40%" height={28} />
          <ShimmerBox width="100%" height={45} borderRadius="12px" />

          {[...Array(6)].map((_, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                animation: `fadeIn 0.5s ease forwards ${i * 0.1}s`,
                opacity: 0,
              }}
            >
              <ShimmerBox width={54} height={54} borderRadius="50%" />
              <Box sx={{ flex: 1 }}>
                <ShimmerBox width="70%" height={14} mb={1} />
                <ShimmerBox width="40%" height={10} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Premium Liquid Main Loader */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(circle at center, #ffffff 0%, #efeae2 100%)",
          position: "relative",
        }}
      >
        {/* The Morphing Blob */}
        <Box
          sx={{
            position: "relative",
            width: 200,
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              bgcolor: "rgba(0, 168, 132, 0.1)",
              animation:
                "morph 8s infinite alternate, spin 12s infinite linear",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "80%",
              height: "80%",
              bgcolor: "rgba(0, 168, 132, 0.2)",
              animation:
                "morph 6s infinite alternate-reverse, spin 8s infinite linear reverse",
            }}
          />

          {/* Inner Liquid SVG */}
          <svg width="60" height="60" viewBox="0 0 50 50" style={{ zIndex: 2 }}>
            <circle
              cx="25"
              cy="25"
              r="22"
              fill="none"
              stroke="#00a884"
              strokeWidth="1"
              strokeDasharray="10 20"
              opacity="0.3"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 25 25"
                to="360 25 25"
                dur="10s"
                repeatCount="indefinite"
              />
            </circle>
            <path
              d="M25 15C19.48 15 15 19.48 15 25C15 30.52 19.48 35 25 35C30.52 35 35 30.52 35 25"
              stroke="#00a884"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 25 25"
                to="360 25 25"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </Box>

        <Box sx={{ mt: 6, textAlign: "center", animation: "fadeIn 1s ease" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#111b21",
              letterSpacing: "2px",
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Establishing
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
            {[0, 1, 2].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: 6,
                  height: 6,
                  bgcolor: "#00a884",
                  borderRadius: "50%",
                  animation: `fadeIn 0.6s infinite alternate ${dot * 0.2}s`,
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Footer Branding */}
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            opacity: 0.4,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>
          <Typography
            sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "1px" }}
          >
            AES-256 BIT ENCRYPTION
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatSkeleton;
